import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, getISOWeek } from 'date-fns';
import type { BalticHistoryPoint } from '@/types/baltic.types';
import { Card } from '@/components/ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  getChartDatasets,
  formatChartTooltipValue,
  formatChartTickValue,
} from '@/utils/balticUtils';

interface BalticChartProps {
  data: BalticHistoryPoint[];
  loading?: boolean;
  showTCE?: boolean;
  selectedPeriod: 'daily' | 'weekly' | 'monthly' | 'yearly';
}

function formatChartDateLabel(
  dateValue: string,
  period: BalticChartProps['selectedPeriod']
): string {
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return dateValue;

  switch (period) {
    case 'daily':
      return format(parsedDate, 'dd/MM/yyyy');
    case 'weekly':
      return `Week ${getISOWeek(parsedDate)}`;
    case 'monthly':
      return format(parsedDate, 'MMM yyyy');
    case 'yearly':
      return format(parsedDate, 'yyyy');
    default:
      return dateValue;
  }
}

export function BalticChart({
  data,
  loading = false,
  showTCE = false,
  selectedPeriod,
}: BalticChartProps) {
  const isNarrow = useMediaQuery('(max-width: 639px)');

  // Get datasets configuration based on Angular logic, dropping series with no data in the
  // loaded range (e.g. TI for routes that are not TI assessed) so they get no line and no legend
  const datasets = getChartDatasets(showTCE).filter(({ key }) =>
    data.some((point) => point[key] !== undefined && point[key] !== null)
  );

  // Transform data for chart display (matching Angular logic)
  const chartData = data.map((point) => {
    const transformed: Record<string, string | number | null> = {
      date: formatChartDateLabel(point.date, selectedPeriod),
      fullDate: point.date,
    };

    // Add data for each dataset key
    // Keep null values as null (don't convert to 0) - this creates gaps in the chart
    datasets.forEach(({ key, label }) => {
      const value = point[key];
      transformed[label] = value !== undefined && value !== null ? value : null;
    });

    return transformed;
  });

  if (loading) {
    return (
      <Card noPadding className="p-3 sm:p-4">
        <div className="flex h-64 items-center justify-center sm:h-80">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
        </div>
      </Card>
    );
  }

  if (data.length === 0 || datasets.length === 0) {
    return (
      <Card className="p-3 sm:p-4">
        <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400 sm:h-80">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 0 01-2-2z"
              />
            </svg>
            <p className="mt-2 text-sm sm:text-base">No chart data available</p>
            <p className="text-xs sm:text-sm">Select a route to view historical data</p>
          </div>
        </div>
      </Card>
    );
  }

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ dataKey: string; value: number; color: string }>;
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div className="max-w-xs rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800 sm:p-3">
          <p className="mb-1 text-xs font-medium text-gray-900 dark:text-white sm:mb-2 sm:text-sm">
            {label}
          </p>
          {payload.map((entry, index: number) => (
            <p key={index} className="text-xs sm:text-sm" style={{ color: entry.color }}>
              {`${entry.dataKey}: ${formatChartTooltipValue(entry.value, showTCE, 2)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({
    payload,
  }: {
    payload?: Array<{ value?: string | number; color?: string }>;
  }) => {
    if (!payload?.length) return null;

    return (
      <div className="mb-2 flex items-center justify-center gap-4 text-xs sm:gap-5 sm:text-sm">
        {payload.map((entry, index) => (
          <div key={`${entry.value}-${index}`} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-10 border-2 border-solid"
              style={{ borderColor: entry.color ?? '#6B7280' }}
            />
            <span className="text-gray-700 dark:text-gray-300">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const chartMargin = isNarrow
    ? { top: 8, right: 8, left: 0, bottom: 4 }
    : { top: 10, right: 10, left: 0, bottom: 5 };

  return (
    <Card noPadding className="p-2 sm:p-4">
      <div className="h-80 w-full sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: isNarrow ? 10 : 12 }}
              className="text-gray-600 dark:text-gray-300"
              height={isNarrow ? 28 : 32}
              tickMargin={isNarrow ? 4 : 8}
              minTickGap={isNarrow ? 12 : 24}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: isNarrow ? 10 : 12 }}
              className="text-gray-600 dark:text-gray-300"
              tickFormatter={(value) => formatChartTickValue(value, showTCE, 2)}
              width={isNarrow ? 46 : 80}
              tickMargin={isNarrow ? 2 : 8}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" align="center" content={<CustomLegend />} />
            {datasets.map((dataset) => (
              <Line
                key={dataset.label}
                type="monotone"
                dataKey={dataset.label}
                stroke={dataset.color}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
